import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { StatusResponse } from '../types/status';
import MonitorList from './MonitorList';
import '../css/panel.css';
import { cx } from '@emotion/css';

const fetchStatus = async (): Promise<StatusResponse> => {
  const response = await axios.get<StatusResponse>(
    `${import.meta.env.VITE_API_URL}/v2/json/status-monitors.json`,
  );
  return response.data;
};

const StatusPage = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['status'],
    queryFn: fetchStatus,
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  const monitors = data?.data;
  const downMonitors = monitors?.filter(
    (monitor) => monitor.status.toUpperCase() === 'DOWN',
  );
  const spaceDashboardDown = downMonitors?.some(
    (monitor) => monitor.friendlyName === 'Space Dashboard',
  );

  const hasDownMonitors = React.useMemo(() => {
    return downMonitors && downMonitors.length > 0;
  }, [downMonitors]);

  const statusWrapperClasses = React.useMemo(() => {
    return cx('status-wrapper', 'panel', {
      'has-down-monitors': hasDownMonitors,
      'space-dashboard-down': spaceDashboardDown,
    });
  }, [hasDownMonitors, spaceDashboardDown]);

  const statusMessage = React.useMemo(() => {
    if (isLoading) {
      return 'Loading...';
    }
    if (isError) {
      return 'Something went wrong, please try again later.';
    }
    if (!data || !data.data) {
      return 'No data available';
    }
    if (hasDownMonitors) {
      if (spaceDashboardDown) {
        return 'Space Dashboard is currently offline';
      } else {
        return 'Some space data is momentarily unavailable';
      }
    }
    return 'All systems operational';
  }, [isLoading, isError, data, hasDownMonitors, spaceDashboardDown]);

  return (
    <>
      <div className={statusWrapperClasses}>
        <a
          href="https://spacedashboard.com"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Space Dashboard"
          className="space-dashboard-link"
        >
          <img
            src="/img/space-dashboard.svg"
            width="50"
            height="50"
            alt="Space Dashboard"
            loading="eager"
          />
        </a>
        <div className="status-content">
          <h2 className="operational-status">{statusMessage}</h2>
          {error && (
            <p style={{ color: '#ff6b6b', margin: 0 }}>
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          )}
          {data?.date_updated && (
            <h4 className="operational-status-updated">
              Updated: <span className="datetime">{data.date_updated} UTC</span>
            </h4>
          )}
        </div>
      </div>
      <MonitorList monitors={monitors ?? []} />
    </>
  );
};

export default StatusPage;
